import { FeatureName, Media, useIsFeatureEnabled } from '@gripp/shared-logic';
import {
    ColumnData,
    SelectionStatus,
    SlideAction,
    SlideLayout,
    useSlideLayout,
    ViewMode,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { ProductDetail } from './productDetail';
import { ProductsOld } from './products-old';
import { ProductsComponent } from './productsComponent';
import { ProductsFilters } from './productsFilters';
import { SelectedProductRow } from './productsList';
import { ProductsSelectionPanelMain } from './productsSelectionPanel';

export const Products = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const { '*': splat } = useParams();

  if (isCropCircleEnabled) {
    return <ProductsBase />;
  }

  if (splat) {
    return (
      <View style={legacyDetailStyles.container}>
        <ProductDetail productId={splat} />
      </View>
    );
  }

  return <ProductsOld />;
};

const legacyDetailStyles = StyleSheet.create({
  container: { flex: 1, flexDirection: 'column' },
});

const ProductDetailWithSlideLayout = ({
  productId,
  onFilePress,
}: {
  productId: string;
  onFilePress?: (media: Media, allImages: Media[]) => void;
}) => {
  const { setColumnTitle } = useSlideLayout();
  return (
    <ProductDetail
      productId={productId}
      setColumnTitle={setColumnTitle}
      onFilePress={onFilePress}
    />
  );
};

export const ProductsBase = ({
  rightColumn: rightColumnOverride,
  viewMode: viewModeOverride,
}: {
  rightColumn?: ColumnData;
  viewMode?: ViewMode;
} = {}) => {
  const { t, i18n } = useTranslation();
  const { '*': productIdParam } = useParams();
  // `/products/filters` matches the `*` wildcard — treat it as "no product id"
  // so the detail panel doesn't try to load a product with that literal.
  const productId =
    productIdParam && productIdParam !== 'filters' ? productIdParam : undefined;
  const navigate = useNavigate();

  const [rowsSelected, setRowsSelected] = useState<SelectedProductRow[]>([]);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );
  const hasSelection = rowsSelected.length > 0;

  useEffect(() => {
    if (!hasSelection) {
      setSelectionStatus((prev) => (prev === 'reset' ? 'reset' : 'idle'));
    }
  }, [hasSelection]);

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const leftColumn = {
    title: 'product.list.products',
    mainComponent: (
      <ProductsComponent
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
      />
    ),
    headerComponent: <ProductsFilters />,
    disableScroll: true,
  };

  // Selection panel takes priority over every other right-column mode.
  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection) return undefined;

    const count = rowsSelected.length;
    const title =
      count === 1
        ? t('product.topBar.productSelected', {
            defaultValue: '1 product selected',
          })
        : t('product.topBar.productsSelected', {
            count,
            defaultValue: `${count} products selected`,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <ProductsSelectionPanelMain productIds={rowsSelectedIds} />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    hasSelection,
    rowsSelected,
    rowsSelectedIds,
    clearSelection,
    t,
    i18n,
  ]);

  const handleFilePress = useCallback(
    (media: Media, _allImages: Media[]) => {
      navigate(`/products/${productId}/files/${media.id}`);
    },
    [navigate, productId]
  );
  const detailRightColumn: ColumnData | undefined = productId
    ? {
        title: 'product.list.products',
        mainComponent: (
          <ProductDetailWithSlideLayout
            productId={productId}
            onFilePress={handleFilePress}
          />
        ),
        disableScroll: true,
        showBackButton: true,
        backButtonTransition: SlideAction.SINGLE,
        onBackButtonPress: () => {
          navigate('/products');
        },
      }
    : undefined;

  // Priority: selection > caller override (filters page) > product detail.
  const resolvedRightColumn =
    selectionRightColumn ?? rightColumnOverride ?? detailRightColumn;
  const resolvedViewMode =
    viewModeOverride ??
    (hasSelection || rightColumnOverride || productId
      ? ViewMode.DUAL
      : ViewMode.SINGLE);

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={resolvedRightColumn}
      viewMode={resolvedViewMode}
    />
  );
};
